/**
 * @module app.um
 */

import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { apiCall } from 'app/shared/lib/essette-api';
import { ComponentBase, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { fireEvents } from 'app/shared/lib/fire-events';
import { Toaster } from 'app/shared/lib/toaster';
import { deepClone } from 'app/shared/lib/utilities';
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { AuthAutoDeterminationEvent, AuthChangeEvent, AuthExternalSystemChangeEvent, AuthReloadEvent, IAuthChangeCustomEventDetail } from 'app/um/lib/events';
import { AuthService } from 'app/um/services/auth-service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { activationStrategy, ActivationStrategyType } from 'aurelia-router';
import { CwqiSavedGuidelinesEvent } from 'app/ext/cwqi/cwqi-manager';
import { ISaveServiceOptions } from 'app/um/components/auth/save-service';
import { defaultOptions, IServiceGridOptions } from 'app/um/components/auth/service-grid';
import { EssTableCustomElement, ExpandEvent } from 'app/shared/elements/ess-table';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import * as UM from 'app/um/models/essette-api';
import * as MCG from 'app/mcg/models/essette-api';
import {
    createDeterminationsCache,
    IDeterminationsCache
} from 'app/um/cache/determinations-cache';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';

@autoinject()
@UseContainerCache({ cacheFactory: createDeterminationsCache })
export class DiagsServicesPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected cacheKey = 'UM_AUTH_DETERMINATIONS_CACHE';
    declare cache: IDeterminationsCache;

    protected auth: UM.Auth | null = null;
    protected cwqiEpisodeGuidelines: MCG.CwqiEpisodeGuideline[] = [];
    protected get authId(): string | null {
        return this.auth && this.auth.id || null;
    }

    protected editedAuth: UM.Auth | null = null;
    protected readonly: boolean = false;
    protected showCWQIGuidelines: boolean = false;

    private subscriptions: Array<Subscription> = [];
    private sortingDeterminations: Sorting = new Sorting();

    protected serviceGridOptions: IServiceGridOptions = defaultOptions;
    protected dentalServiceGridOptions: IServiceGridOptions = defaultOptions;
    protected saveServiceOptions: ISaveServiceOptions = {
        showStatus: true,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false
    };

    protected saveDentalServiceOptions: ISaveServiceOptions = {
        showStatus: true,
        showStatusReason: false,
        showQtyApproved: false,
        showApprovedCharges: false,
        showModifiers: false
    };

    protected authServiceStatuses: UM.AuthServiceStatus[] = [];
    protected servicesStatusReason: IAuthContextSettingsDetails | null = null;

    protected determinationHistoryDetails: Record<string, boolean> = {};

    protected $determinationsTable!: EssTableCustomElement<UM.AuthDeterminationHistory>;

    ////****************** Element References ***************************************////
    protected $element!: Element;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly authService: AuthService,
        private readonly context: AuthPageContext,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) throw new Error('Missing or invalid authId parameter');

        return true;
    }

    protected async activate({ authId }: { authId: string }): Promise<void> {
        // If Provider Portal, do not display CWQI Guidelines
        this.showCWQIGuidelines = this.env.application.code !== 'Portal.Providers';
        if(this.authId !== authId) {
            this.auth = this.context.auth;
            this.loadGuidelines();

            this.editedAuth = deepClone(this.auth);
        }

        this.checkPermissions();

        this.subscriptions.push(
            this.events.subscribe(AuthReloadEvent, (e: AuthReloadEvent) => this.handleAuthReloadEvent(e)),
            this.events.subscribe(AuthAutoDeterminationEvent, (e: AuthReloadEvent) => this.handleAuthReloadEvent(e)),
            this.events.subscribe(AuthExternalSystemChangeEvent, () => this.reloadAll()),
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => this.handleAuthReloadEvent(e)),
            this.events.subscribe(CwqiSavedGuidelinesEvent, (e: CwqiSavedGuidelinesEvent) => this.handleCWQIGuidelinesChangedEvent())
        );

        this.serviceGridOptions.showModifiers = this.context.settings.servicesModifier.visible || false;
        this.dentalServiceGridOptions.showModifiers = this.context.settings.servicesModifier.visible || false;

        this.saveServiceOptions = {
            showStatus: true,
            showStatusReason: this.context.settings.servicesStatusReason.visible || false,
            showQtyApproved: true,
            showApprovedCharges: this.context.settings.serviceApprovedCharges.visible || false,
            showModifiers: this.context.settings.servicesModifier.visible || false
        };

        this.saveDentalServiceOptions = {
            showStatus: true,
            showStatusReason: this.context.settings.servicesDentalStatusReason.visible || false,
            showQtyApproved: true,
            showApprovedCharges: false,
            showModifiers: this.context.settings.servicesDentalModifier.visible || false
        };
    }

    protected bind(): void {
        this.loadLookups();
        // Check if cached determinations data is still fresh to use and related to the current auth id
        if(this.cache.authId === this.authId && this.cache.isFresh('determinations')) {
            if(!!this.cache.sorting) {
                this.sortingDeterminations = this.cache.sorting;
            }
        }
        else {
            this.loadDeterminations();
        }
    }

    protected attached(): void {
        if(this.sortingDeterminations.entries.length > 0) {
            this.$determinationsTable.applySorting(this.sortingDeterminations);
        }
    }

    /** Needed on this component to force our various custom elements to reload when switching auths via the auth tabs */
    protected determineActivationStrategy(): ActivationStrategyType {
        return activationStrategy.replace;
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected handleAuthReloadEvent(e: AuthReloadEvent): void {
        this.auth = this.context.auth;
        this.loadGuidelines();
        this.editedAuth = deepClone(this.auth);
        this.checkPermissions();
        this.reloadDeterminations();
    }

    protected handleCWQIGuidelinesChangedEvent(): void {
        this.reloadAll();
    }

    protected determinationHistoryDetailLoaded(event: ExpandEvent<UM.AuthDeterminationHistory>): void {
        const item = event.detail.item;
        const items = event.detail.items;

        if(item && items.includes(item)) {
            this.determinationHistoryDetails[item.id!] = true;
        }
    }

    ////****************** Functions ************************************************////

    /** Handles the grid's sort event for the determination table */
    protected applySortDeterminations(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const sorting = this.sortingDeterminations;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;

            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) {
                return;
            }
            else {
                sorting.add({
                    reference: property,
                    direction: sortDirection
                });
            }
        }

        this.reloadDeterminations();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
        this.servicesStatusReason = this.context.settings.servicesStatusReason;
    }

    protected reloadDeterminations(): void {
        if(this.authId) this.loadDeterminations();
    }

    protected loadGuidelines(): void {
        this.cwqiEpisodeGuidelines = this.context.cwqiEpisodeGuidelines.filter(guideline => guideline.indicationsGuideline);
    }

    protected async reloadAll(): Promise<void> {
        await this.context.refreshAuthSettings();
        this.auth = this.context.auth;
        this.loadGuidelines();

        this.editedAuth = deepClone(this.auth);
        this.reloadDeterminations();
    }

    protected applyTableDisplay(determinations: UM.AuthDeterminationHistory[]): UM.AuthDeterminationHistory[] {
        return determinations.map((determinationHistory) => {
            const determinationDate = determinationHistory.determination?.timestamp ? new Date(determinationHistory.determination?.timestamp) : null;
            const display = determinationDate ?
                `${determinationDate.toLocaleDateString('en-US')} ${determinationDate.toLocaleTimeString('en-US')}` : null;
            return {
                ...determinationHistory,
                $essTableDisplay: display
            } as UM.AuthDeterminationHistory;
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveAuthFlags(): Promise<void> {
        if(!this.editedAuth || !this.editedAuth.flag) return;

        const saveAuth: UM.Auth = {
            id: this.editedAuth.id,
            flag: {
                potentialQualityIssue: this.editedAuth.flag.potentialQualityIssue,
                potentialThirdPartyLiability: this.editedAuth.flag.potentialThirdPartyLiability,
                potentialCaseManagement: this.editedAuth.flag.potentialCaseManagement
            }
        };

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.authService.saveAuth(saveAuth);
            isSuccessful = response.isSuccessful;
            this.context.triggerCorrespondenceRules();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.flag.$long_plural', isSuccessful, 2);
            this.state.endSave();
        }

    }

    @apiCall
    protected async saveChiefComplaint(): Promise<void> {
        if(!this.editedAuth) return;

        const chiefComplaint: UM.AuthChiefComplaint = {
            auth: { id: this.editedAuth.id },
            text: this.editedAuth.chiefComplaint ? (this.editedAuth.chiefComplaint.text && this.editedAuth.chiefComplaint.text.length > 0 ? this.editedAuth.chiefComplaint.text : null) : null
        } as UM.AuthChiefComplaint;

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.authService.saveChiefComplaint(chiefComplaint);
            isSuccessful = response.isSuccessful;

            const detail: IAuthChangeCustomEventDetail = { authReloaded: false, triggerRules: true };
            fireEvents(this.$element, ['auth-change'], detail);
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.chiefComplaint.$', isSuccessful);
            this.state.endSave();
        }
    }


    @apiCall
    private async loadLookups(): Promise<void> {
        if(this.readonly) return;

        try {
            this.state.beginLoad();
            const includes = ['determination.status'];
            const fields = ['code', 'description', 'determination.status.determination.result'];

            if(this.servicesStatusReason?.visible) {
                includes.push('reasons');
                fields.push('reasons.description');
            };

            const response = await this.authService.loadAuthServiceStatuses({ all: true }, includes, {}, fields);
            this.authServiceStatuses = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }


    @apiCall
    private async loadDeterminations(): Promise<void> {
        if(!this.authId) return;

        try {
            this.state.beginLoad();

            const options: Partial<IQueryLoadOptions> = {
                all: true,
                sorting: this.sortingDeterminations
            };

            const includes = [
                'authStatus',
                'determination.source',
                'determination.user',
            ];

            const fields = [
                'authStatus.description',
                'determination.source.description',
                'determination.timestamp',
                'determination.user.description'
            ];

            const response = await this.authService.loadDeterminationsByAuth(this.authId, options, includes, fields);
            this.cache.determinations = this.applyTableDisplay(response.results);
            this.cache.authId = this.authId;
            this.cache.sorting = this.sortingDeterminations;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async runDeterminationRules(): Promise<void> {
        try {
            this.state.beginSave();
            await this.authService.autoDetermination({ id: this.authId });
            this.toaster.info().message(this.i18n.tr('ui:labels.ran', { what: '$t(um:determinationRules.$)' })).show();
            await this.reloadAll();
            this.events.publish(new AuthReloadEvent());
            this.events.publish(new AuthChangeEvent(true));
            this.context.triggerCorrespondenceRules();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
